// Job-stopping material shortages — the urgent subset of GET /materials/below_min.
//
// A material stops jobs when the quantity open jobs have RESERVED exceeds what is
// on hand plus what is already on a Committed PO:
//
//     short = reserved − (qoh + inbound)          (> 0 ⇒ job-stopping)
//
// Every unit of `short` has to be bought (or a job re-planned) before the jobs
// that reserved it can run. That is a different question from the Low Stock
// Material Alerts card, which asks whether the AVAILABLE position has slipped
// under a safety floor (`minimum_available`) — a floor can be breached with every
// job still fully covered. The two cards deliberately overlap: a job-stopper is
// always also below its floor (a floor is never negative), and it must not
// disappear from the floor list just because it was promoted here.
//
// `reserved`, `qoh` and `inbound` are the SAME numbers /materials/below_min
// already returns — `materials.reserved_quantity` is kept live at job creation,
// release and consumption and reconciled daily — so this is derived client-side
// from one payload rather than adding a second server-side definition of
// "reserved" that could drift from the first.
//
// Rows with NOTHING reserved never qualify, even at negative stock: a negative
// on-hand with no open-job demand is a stock-record error, not a stalled job.
// It stays visible in Low Stock Material Alerts.
//
// STOCK DROPS (since 2026-10-19): a material some cut sheet MAKES (a 48x60 left
// by a HALF sheet, a sheet-division size) comes with `made_in_house: true`,
// `drop_inbound` (pieces the open laser tasks will make) and `drop_makers`
// (the cut sheets that make it). Those pieces count as inbound, and the row is
// never bought here: the drop planner (POST /materials/drop_plan) switches jobs
// to their full-sheet cut sheet or adds a sheet-division job instead.

const num = (v) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

/**
 * Units of `row` that open jobs need and nothing covers. 0 when the material is
 * not job-stopping. Whole units — a sheet or stick is bought whole.
 */
export function jobStopShortfall(row) {
  if (!row) return 0
  const reserved = num(row.reserved)
  if (reserved <= 0) return 0
  const short = reserved - (num(row.qoh) + num(row.inbound) + num(row.drop_inbound))
  return short > 0 ? Math.ceil(short) : 0
}

/**
 * Card rows from the /materials/below_min payload, deepest hole first.
 *
 * Vendor: the materials list's `supplier` (the preferred material_vendors row,
 * which is what the Low Stock Material Alerts card keys on) wins; the payload's
 * own `vendor_name` — the same subquery — is the fallback when the materials
 * list has not loaded yet. A material with neither is KEPT and flagged
 * `hasVendor: false`: the buyer must still see it, they just cannot PO it here.
 *
 * `recommendedQty` is the shortfall — the minimum that unblocks the jobs —
 * not the top-up to `minimum_available`; safety stock is the other card's job.
 */
export function deriveJobStopShortages(belowMinRows, allMaterials) {
  const byId = new Map()
  for (const m of allMaterials || []) {
    if (m && m.material_id != null) byId.set(m.material_id, m)
  }
  const rows = []
  for (const m of belowMinRows || []) {
    const shortfall = jobStopShortfall(m)
    if (shortfall <= 0) continue
    const mat = byId.get(m.material_id)
    const vendor = String((mat && mat.supplier) || m.vendor_name || "").trim()
    const madeInHouse = !!m.made_in_house
    rows.push({
      ...m,
      qoh: num(m.qoh),
      inbound: num(m.inbound),
      reserved: num(m.reserved),
      dropInbound: num(m.drop_inbound),
      dropMakers: Array.isArray(m.drop_makers) ? m.drop_makers : [],
      madeInHouse,
      shortfall,
      recommendedQty: shortfall,
      vendorOverride: vendor,
      hasVendor: vendor !== "",
      selected: false,
    })
  }
  rows.sort((a, b) =>
    b.shortfall - a.shortfall
    || String(a.material_code || "").localeCompare(String(b.material_code || "")))
  return rows
}

/** Rows a PO can be made for: bought materials only (a stock drop is cut in-house). */
export function orderableJobStopRows(rows) {
  return (rows || []).filter(r => !r.madeInHouse)
}

/**
 * One line per thing a drop-planner run (POST /materials/drop_plan) did, for the card.
 * `report` is the endpoint's answer; anything missing reads as "nothing".
 */
export function dropPlanSummary(report) {
  if (!report) return []
  if (report.skipped) return [`Not run: ${report.skipped}.`]
  const lines = []
  const applied = report.applied !== false
  for (const s of report.switches || []) {
    if (applied && !s.applied) {
      lines.push(`Job #${s.master_job_num}: not switched (${s.error || 'refused'})`)
    } else {
      lines.push(`Job #${s.master_job_num}: ${s.from_cut_sheet} → ${s.to_cut_sheet} (makes a ${s.drop_material_code})`)
    }
  }
  for (const d of report.division_jobs || []) {
    const makes = (d.makes || []).map(m => `${m.pieces} × ${m.material_code}`).join(', ')
    if (applied && !d.applied) {
      lines.push(`${d.cut_sheet_name}: job not created (${d.error || 'refused'})`)
    } else {
      lines.push(`${d.master_job_num ? `New job #${d.master_job_num}` : 'New job'}: ${d.sheets} sheet(s) of ${d.cut_sheet_name} (${makes})`)
    }
  }
  for (const u of report.unresolved || []) {
    lines.push(`${u.code}: still short ${u.short} — no job can be switched and no cut sheet makes it`)
  }
  for (const p of report.parents_short || []) {
    lines.push(`${p.code}: now short ${p.short} sheet(s) — order them (the switched jobs cut from it)`)
  }
  if (!lines.length) lines.push('Nothing to do: every stock drop the open jobs need is in stock or already queued.')
  if (report.applied === false && report.mode === 'off') lines.unshift('Drop planner is OFF (app setting): this is what it would do.')
  return lines
}

/** The heading for a drop-planner result: a preview, a run that changed things, or one that didn't run. */
export function dropPlanHeading(report, preview) {
  if (preview) return 'Drop planner — what it would do:'
  if (!report || report.skipped || report.applied === false) return 'Drop planner — not run:'
  return 'Drop planner — done:'
}
